// src/utils/dateFilters.js

/**
 * Common date filter configurations for dashboards, reports, and modals.
 * Each filter returns [startDate, endDate].
 */
export const dateFilters = {
  All: null,

  Today: () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return [start, now];
  },

  Yesterday: () => {
    const now = new Date();
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1
    );
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - 1,
      23,
      59,
      59
    );
    return [start, end];
  },

  "This Week": () => {
    const now = new Date();
    const day = now.getDay(); // 0 (Sun) to 6 (Sat)

    // Start of this week = today (if Sunday) or last Sunday
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    start.setDate(now.getDate() - day);

    // End of this week = next Saturday
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return [start, end];
  },

  "Last Week": () => {
    const now = new Date();
    const day = now.getDay(); // 0 (Sun) to 6 (Sat)

    // End of last week = yesterday
    const end = new Date(now);
    end.setDate(now.getDate() - day - 1);
    end.setHours(23, 59, 59, 999);

    // Start of last week = last Sunday before that
    const start = new Date(end);
    start.setDate(end.getDate() - 6);
    start.setHours(0, 0, 0, 0);

    return [start, end];
  },

  "This Month": () => {
    const now = new Date();
    return [new Date(now.getFullYear(), now.getMonth(), 1), now];
  },

  "Last Month": () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
    return [start, end];
  },

  "This Quarter": () => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), quarter * 3, 1);
    const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  },

  "This Year": () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1); // Jan 1, 00:00:00
    const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59); // Dec 31, 23:59:59
    return [start, end];
  },

  "Last Year": () => {
    const now = new Date();
    const start = new Date(now.getFullYear() - 1, 0, 1);
    const end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
    return [start, end];
  },

  // Future-friendly custom range (set externally)
  "Custom Range": (start, end) => [start, end],
};

/**
 * Utility to check if a given record date is within a selected filter.
 * @param {Date} recordDate
 * @param {string} filterKey
 * @param {Date|null} customStart
 * @param {Date|null} customEnd
 */
export const isWithinDateFilter = (
  recordDate,
  filterKey,
  customStart = null,
  customEnd = null
) => {
  if (!filterKey || filterKey === "All" || !dateFilters[filterKey]) return true;

  const [start, end] =
    filterKey === "Custom Range"
      ? [customStart, customEnd]
      : dateFilters[filterKey]();

  return recordDate >= start && recordDate <= end;
};
